/**
 * Utilities for handling API responses consistently
 */
import { User } from '../services/authService';

export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface UserResponse {
  user?: User;
  data?: {
    user?: User;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Extracts user data from various API response formats
 */
export function extractUser(data: unknown): User {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid user data: expected object');
  }

  const obj = data as UserResponse;

  // Try different response structures
  if (obj.data?.user) {
    return obj.data.user;
  }
  if (obj.data && 'id' in obj.data && 'email' in obj.data) {
    // Type guard: check if obj.data has required User properties
    const dataObj = obj.data as Record<string, unknown>;
    if (
      typeof dataObj.id === 'string' &&
      typeof dataObj.email === 'string' &&
      typeof dataObj.username === 'string' &&
      typeof dataObj.role === 'string'
    ) {
      return dataObj as unknown as User;
    }
  }
  if (obj.user) {
    return obj.user;
  }
  if ('id' in obj && 'email' in obj) {
    // Type guard: check if obj has required User properties
    const objAsRecord = obj as Record<string, unknown>;
    if (
      typeof objAsRecord.id === 'string' &&
      typeof objAsRecord.email === 'string' &&
      typeof objAsRecord.username === 'string' &&
      typeof objAsRecord.role === 'string'
    ) {
      return objAsRecord as unknown as User;
    }
  }

  throw new Error('Invalid user data: user object not found in response');
}

/**
 * Extracts data from API response, handling various formats
 */
export function extractApiData<T>(response: unknown): T {
  if (typeof response !== 'object' || response === null) {
    throw new Error('Invalid API response: expected object');
  }

  const obj = response as ApiResponse<T>;

  if (obj.data !== undefined) {
    return obj.data;
  }

  // If no data field, assume the response itself is the data
  return response as T;
}
